// src/pages/pengamatan/buah-alami/BuahAlami.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Snackbar,
    Table,
    TableContainer,
    TablePagination,
} from "@mui/material";
import { useOutletContext } from "react-router-dom";
import useAuthCheck from "../../../hooks/useAuthCheck";

import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";
import BuahAlamiTableHeader from "./BuahAlamiTableHeader";
import BuahAlamiTableBody from "./BuahAlamiTableBody";
import BuahAlamiToolbar from "./BuahAlamiToolbar";
import useBuahAlamiData from "./hooks/useBuahAlamiData";
import useSearch from "./hooks/useSearch"; // GUNAKAN YANG SEDERHANA
import "./css/BuahAlami.css";

const BuahAlami = () => {
    const { user, loading: authLoading, userProfile } = useAuthCheck();
    const { breadcrumbs } = useOutletContext();

    // State untuk tabs - DIPERBAIKI: Gunakan localStorage untuk menyimpan tab terakhir
    const [currentTab, setCurrentTab] = useState(() => {
        // Coba ambil dari localStorage saat komponen pertama kali dimuat
        const savedTab = localStorage.getItem('buah-alami-current-tab');
        // Validasi tab yang disimpan
        const validTabs = ['Pending', 'Approved', 'Processed', 'Closing'];
        return validTabs.includes(savedTab) ? savedTab : 'Pending';
    });

    // Gunakan custom hook untuk mendapatkan data
    const {
        orders,
        pendingOrders,
        approvedOrders,
        processedOrders,
        closingOrders,
        loadingOrders,
        snackbar,
        setSnackbar,
        userJabatan,
        userRoleNames,
        updateStatusPengamatan,
    } = useBuahAlamiData(user, userProfile);

    // State untuk data yang sedang aktif (berdasarkan tab)
    const [currentTabData, setCurrentTabData] = useState([]);

    // Gunakan custom hook untuk pencarian - INISIALISASI DENGAN ARRAY KOSONG
    const {
        searchTerm,
        setSearchTerm,
        filteredData: filteredOrders,
        setFilteredData: setFilteredOrders,
        handleSearch,
        handleResetFilter,
        updateData
    } = useSearch([]);

    const [selectedItems, setSelectedItems] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    // State untuk pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // FUNGSI BARU: Simpan tab ke localStorage
    const saveTabToLocalStorage = (tab) => {
        try {
            localStorage.setItem('buah-alami-current-tab', tab);
        } catch (error) {
            console.warn('Gagal menyimpan tab ke localStorage:', error);
        }
    };

    // Filter data berdasarkan tab yang aktif
    const filterDataByTab = () => {
        switch (currentTab) {
            case "Pending":
                return pendingOrders;
            case "Approved":
                return approvedOrders;
            case "Processed":
                return processedOrders;
            case "Closing":
                return closingOrders;
            default:
                return [];
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // FUNGSI PENCARIAN YANG BERFUNGSI
    const handleSearchWrapper = (term) => {
        handleSearch(term, currentTabData);
        setPage(0); // Reset ke halaman pertama saat mencari
    };

    // FUNGSI RESET FILTER YANG BERFUNGSI
    const handleResetFilterWrapper = () => {
        handleResetFilter(currentTabData);
        setPage(0); // Reset ke halaman pertama saat reset filter
    };

    // Update data ketika tab berubah atau data orders berubah
    useEffect(() => {
        const tabData = filterDataByTab();
        // Urutkan berdasarkan tanggal terbaru (descending)
        const sortedData = [...tabData].sort((a, b) => {
            const dateA = a.tanggal_pengamatan_sort || "00000000";
            const dateB = b.tanggal_pengamatan_sort || "00000000";
            return dateB.localeCompare(dateA); // Terbaru di atas
        });
        
        setCurrentTabData(sortedData);
        
        // Update data di hook pencarian
        if (searchTerm) {
            // Jika ada search term, apply filter ke data baru
            const filtered = sortedData.filter((order) =>
                order.lokasi && order.lokasi.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredOrders(filtered);
        } else {
            // Jika tidak ada search term, gunakan data asli
            setFilteredOrders(sortedData);
        }
        
        setPage(0); // Reset ke halaman pertama saat ganti tab
        setSelectedItems([]); // Reset selected items saat ganti tab
    }, [currentTab, pendingOrders, approvedOrders, processedOrders, closingOrders, searchTerm, setFilteredOrders]);

    // FUNGSI YANG DIPERBAIKI: Handle perubahan tab - SEKARANG MENYIMPAN KE localStorage
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        saveTabToLocalStorage(newValue); // Simpan tab yang baru dipilih
    };

    // Fungsi untuk handle perubahan halaman
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Fungsi untuk handle perubahan jumlah baris per halaman
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset ke halaman pertama saat mengubah rowsPerPage
    };

    // Data yang ditampilkan berdasarkan pagination
    const paginatedOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // FUNGSI YANG DIPERBAIKI: Cek apakah semua item di halaman saat ini terpilih
    const areAllItemsOnCurrentPageSelected = () => {
        if (paginatedOrders.length === 0) return false;
        return paginatedOrders.every((order) => selectedItems.includes(order.id));
    };

    // FUNGSI YANG DIPERBAIKI: Handle select all - SEDERHANA
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Pilih semua item yang ditampilkan di halaman saat ini
            const currentPageItemIds = paginatedOrders.map((order) => order.id);
            setSelectedItems(currentPageItemIds);
        } else {
            // Hapus semua item dari selectedItems
            setSelectedItems([]);
        }
    };

    useEffect(() => {
        // Hitung tinggi total header (kedua baris)
        const headerRows = document.querySelectorAll(".buah-alami-table-header");
        let totalHeight = 0;

        headerRows.forEach((row) => {
            totalHeight += row.offsetHeight;
        });

        if (totalHeight > 0) {
            const container = document.querySelector(".buah-alami-container");
            if (container) {
                container.style.setProperty("--header-height", `${totalHeight}px`);
            }
        }
    }, [filteredOrders]);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    // FUNGSI YANG DIPERBAIKI: Handle select item - SEDERHANA
    const handleSelectItem = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter((itemId) => itemId !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const handleStatusUpdate = (snackbarData) => {
        setSnackbar(snackbarData);
    };

    // Hitung jumlah data per tab
    const getTabCount = (status) => {
        switch (status) {
            case "Pending":
                return pendingOrders.length;
            case "Approved":
                return approvedOrders.length;
            case "Processed":
                return processedOrders.length;
            case "Closing":
                return closingOrders.length;
            default:
                return 0;
        }
    };

    if (authLoading || loadingOrders) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Anda harus login untuk mengakses halaman ini.</Alert>
            </Box>
        );
    }

    return (
        <Box className="buah-alami-container">
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Card>
                <CardContent sx={{ width: "100%", overflow: "hidden", p: 2 }}>
                    <BuahAlamiToolbar
                        searchTerm={searchTerm}
                        onSearchChange={(e) => handleSearchWrapper(e.target.value)}
                        onResetFilter={handleResetFilterWrapper}
                        anchorEl={anchorEl}
                        onMenuClick={handleClick}
                        onMenuClose={handleClose}
                        open={open}
                        selectedItems={selectedItems}
                        userJabatan={userJabatan}
                        userRoleNames={userRoleNames}
                        updateStatusPengamatan={updateStatusPengamatan}
                        onStatusUpdate={handleStatusUpdate}
                        orders={orders}
                        currentTab={currentTab}
                        onTabChange={handleTabChange}
                        getTabCount={getTabCount}
                        pendingOrders={pendingOrders}
                        approvedOrders={approvedOrders}
                        processedOrders={processedOrders}
                        closingOrders={closingOrders}
                    />

                    <TableContainer className="buah-alami-table-container">
                        <Table stickyHeader className="buah-alami-table">
                            <BuahAlamiTableHeader
                                currentTab={currentTab}
                                selectedItems={selectedItems}
                                orders={paginatedOrders}
                                handleSelectAll={handleSelectAll}
                                areAllItemsOnCurrentPageSelected={areAllItemsOnCurrentPageSelected}
                                userJabatan={userJabatan}
                                userRoleNames={userRoleNames}
                            />
                            <BuahAlamiTableBody
                                orders={paginatedOrders}
                                selectedItems={selectedItems}
                                handleSelectItem={handleSelectItem}
                                loadingOrders={loadingOrders}
                                currentTab={currentTab}
                                onStatusUpdate={handleStatusUpdate}
                                userJabatan={userJabatan}
                                userRoleNames={userRoleNames}
                            />
                        </Table>
                    </TableContainer>

                    {/* TablePagination */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredOrders.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        classes={{
                            select: "TablePagination-select",
                            displayedRows: "TablePagination-displayedRows",
                            actions: "TablePaginationActions-root",
                        }}
                        labelRowsPerPage="Baris per halaman:"
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} dari ${count !== -1 ? count : `lebih dari ${to}`}`
                        }
                    />
                </CardContent>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BuahAlami;